import { Draft, produce } from 'immer';
import { cloneDeep } from 'lodash';

import { Department, FoodLine, FoodTray, FoodTraySize, FoodTrayTemplate } from '../../models';
import { ActionType } from './types';

export interface FoodTrayTemplateEditState {
  currentFoodTrayTemplate: Partial<FoodTrayTemplate>;
  initialFoodTrayTemplate: Partial<FoodTrayTemplate>;
}

export const FOOD_TRAY_INITIAL_STATE: Partial<FoodTray> = {
  columnIndex: 0,
  rowIndex: 0,
  size: FoodTraySize.FULL,
  defaultFoodType: null,
};

export const FOOD_LINE_INITIAL_STATE: Partial<FoodLine> = {
  name: 'Foodline 1',
  index: 0,
  foodTrays: [FOOD_TRAY_INITIAL_STATE as FoodTray],
};

export const FOOD_TRAY_TEMPLATE_INITIAL_STATE: Partial<FoodTrayTemplate> = {
  name: '',
  department: null,
  foodLines: [FOOD_LINE_INITIAL_STATE as FoodLine],
};

// Helpers
const repositionFoodLines = (foodLines: FoodLine[]) => {
  foodLines.forEach((foodLine, index) => {
    foodLine.index = index;
  });
};

// Reducer
const foodTrayTemplateEditReducer = produce(
  (draft: Draft<FoodTrayTemplateEditState>, action) => {
    // Because we use immer, we have to mutate the draft state in place for it to work properly
    // We don't have to return anything because our produce() will automagically do that for us
    let foodLine: FoodLine | undefined;
    switch (action.type) {
      case ActionType.RESET_FOOD_TRAY_TEMPLATE:
        draft.currentFoodTrayTemplate.name = '';
        draft.currentFoodTrayTemplate.foodLines = cloneDeep(FOOD_TRAY_TEMPLATE_INITIAL_STATE.foodLines);
        draft.currentFoodTrayTemplate.department = { id: action.payload.departmentId } as Department;
        draft.currentFoodTrayTemplate.isDeleted = false;
        draft.currentFoodTrayTemplate.createdAt = new Date().toISOString();
        draft.currentFoodTrayTemplate.updatedAt = new Date().toISOString();
        draft.currentFoodTrayTemplate.createdBy = null;
        draft.currentFoodTrayTemplate.updatedBy = null;

        draft.initialFoodTrayTemplate.name = '';
        draft.initialFoodTrayTemplate.foodLines = cloneDeep(FOOD_TRAY_TEMPLATE_INITIAL_STATE.foodLines);
        draft.initialFoodTrayTemplate.department = { id: action.payload.departmentId } as Department;
        draft.initialFoodTrayTemplate.isDeleted = false;
        draft.initialFoodTrayTemplate.createdAt = new Date().toISOString();
        draft.initialFoodTrayTemplate.updatedAt = new Date().toISOString();
        draft.initialFoodTrayTemplate.createdBy = null;
        draft.initialFoodTrayTemplate.updatedBy = null;
        break;

      case ActionType.SET_FOOD_TRAY_TEMPLATE:
        draft.currentFoodTrayTemplate = cloneDeep(action.payload.foodTrayTemplate);
        if (action.payload.replaceInitial) {
          draft.initialFoodTrayTemplate = cloneDeep(action.payload.foodTrayTemplate);
        }
        break;

      case ActionType.SET_FOOD_TRAY_TEMPLATE_PROP:
        (draft.currentFoodTrayTemplate as unknown)[action.payload.propName] = action.payload.propValue;
        break;

      case ActionType.ADD_FOOD_LINE:
        draft.currentFoodTrayTemplate.foodLines = draft.currentFoodTrayTemplate.foodLines || [];
        draft.currentFoodTrayTemplate.foodLines.push({
          ...(FOOD_LINE_INITIAL_STATE as FoodLine),
          index: draft.currentFoodTrayTemplate.foodLines.length,
          name: 'Foodline ' + (draft.currentFoodTrayTemplate.foodLines.length + 1),
          createdAt: new Date().toISOString(),
        });
        break;

      case ActionType.SET_FOOD_LINE_PROP:
        foodLine = (draft.currentFoodTrayTemplate.foodLines || [])[action.payload.foodLineIndex];
        if (!foodLine) {
          return;
        }

        (foodLine as unknown)[action.payload.propName] = action.payload.propValue;
        break;

      case ActionType.REMOVE_FOOD_LINE:
        draft.currentFoodTrayTemplate.foodLines.splice(action.payload.foodLineIndex, 1);
        repositionFoodLines(draft.currentFoodTrayTemplate.foodLines);
        break;

      case ActionType.ADD_FOOD_TRAY_COLUMN:
        foodLine = draft.currentFoodTrayTemplate.foodLines[action.payload.foodLineIndex];
        if (!foodLine) {
          return;
        }

        // Move all columns to the the right, to make room for the new column
        const newColumnIndex: number = action.payload.columnIndex;
        foodLine.foodTrays.forEach(foodTray => {
          if (foodTray.columnIndex >= newColumnIndex) {
            foodTray.columnIndex = foodTray.columnIndex + 1;
          }
        });

        // Insert the new column
        const newFoodTrays = (action.payload.foodTrays as FoodTray[]).map(foodTray => ({
          ...foodTray,
          columnIndex: newColumnIndex,
        }));
        foodLine.foodTrays.push(...newFoodTrays);
        break;

      case ActionType.REMOVE_FOOD_TRAY_COLUMN:
        foodLine = draft.currentFoodTrayTemplate.foodLines[action.payload.foodLineIndex];
        if (!foodLine) {
          return;
        }

        foodLine.foodTrays = foodLine.foodTrays.filter(foodTray => foodTray.columnIndex !== action.payload.columnIndex);
        foodLine.foodTrays.forEach(foodTray => {
          if (foodTray.columnIndex >= action.payload.columnIndex) {
            foodTray.columnIndex = foodTray.columnIndex - 1;
          }
        });
        break;

      case ActionType.SET_FOOD_TRAYS_FOR_COLUMN:
        foodLine = draft.currentFoodTrayTemplate.foodLines[action.payload.foodLineIndex];
        if (!foodLine) {
          return;
        }

        const foodTraysInColumn = foodLine.foodTrays.filter(foodTray => foodTray.columnIndex === action.payload.columnIndex);
        const limitedFoodTrays = foodTraysInColumn.slice(
          0,
          Math.min(action.payload.foodTraySizes.length, foodTraysInColumn.length),
        );
        if (limitedFoodTrays.length < action.payload.foodTraySizes.length) {
          // add extra food trays
          for (let i = limitedFoodTrays.length; i < action.payload.foodTraySizes.length; i++) {
            limitedFoodTrays.push({
              ...(FOOD_TRAY_INITIAL_STATE as FoodTray),
              size: action.payload.foodTraySizes[i],
              rowIndex: i,
            });
          }
        }
        const foodTraysWithCorrectSizes = limitedFoodTrays.map((foodTray, index) => ({
          ...foodTray,
          size: action.payload.foodTraySizes[index],
          columnIndex: action.payload.columnIndex,
        }));
        foodLine.foodTrays = [
          ...foodLine.foodTrays.filter(foodTray => foodTray.columnIndex !== action.payload.columnIndex),
          ...foodTraysWithCorrectSizes,
        ];
        break;

      case ActionType.SET_FOOD_TRAY_PROP:
        foodLine = (draft.currentFoodTrayTemplate.foodLines || [])[action.payload.foodLineIndex];
        if (!foodLine) {
          return;
        }

        const foodTrayIndex = foodLine.foodTrays.findIndex(
          foodTray => foodTray.columnIndex === action.payload.columnIndex && foodTray.rowIndex === action.payload.rowIndex,
        );
        if (foodTrayIndex === -1) {
          return;
        }

        foodLine.foodTrays[foodTrayIndex] = {
          ...foodLine.foodTrays[foodTrayIndex],
          [action.payload.propName]: action.payload.propValue,
        };
        break;

      default:
        // We don't actually need the default case, produce() will simply return the
        // original state if nothing has changed in the draft
        break;
    }
  },
  {
    currentFoodTrayTemplate: FOOD_TRAY_TEMPLATE_INITIAL_STATE,
    initialFoodTrayTemplate: FOOD_TRAY_TEMPLATE_INITIAL_STATE,
  },
);

export default foodTrayTemplateEditReducer;
